import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { X } from 'lucide-react';
import { useToast } from '@/components/ui/toast';
import { isoLocalDate } from '@/lib/domain/format';
import { LEGAL_STATUSES } from '@/lib/domain/legalStatus';
import { useNewDeal } from './newDealStore';
import { useReferenceData } from './useReferenceData';
import { type ComboItem, EntityCombobox } from './EntityCombobox';
import { useCompanies, useOriginators, usePeople } from './useEntities';
import { CompanyInline, OriginatorInline, PersonInline } from './InlineEntityForms';
import { buildCreateDealBody, useCreateDeal } from './useCreateDeal';

const INPUT =
  'w-full rounded border border-vega-line bg-vega-surface px-2.5 py-2 text-[13px] text-vega-ink outline-none';
const LABEL = 'mb-1 block text-[11px] font-medium uppercase tracking-wide text-vega-muted';

const schema = z
  .object({
    codename: z.string().trim().min(1, 'Informe o codinome').max(200),
    legalStatus: z.coerce.number(),
    investmentValue: z.string(),
    thesisSummary: z.string().max(4000),
    leadUserId: z.string().min(1, 'Selecione o responsável'),
    thesisIds: z.array(z.number()),
    teamUserIds: z.array(z.string()),
    targetCompany: z.object({ id: z.string(), name: z.string() }).nullable(),
    externalOriginator: z.object({ id: z.string(), name: z.string() }).nullable(),
    person: z.object({ id: z.string(), name: z.string() }).nullable(),
    isInternalOriginator: z.boolean(),
    internalOriginatorId: z.string().nullable(),
    initialEntryAt: z.string(),
  })
  // Empresa-alvo é opcional (item 1) — sem refine obrigatório.
  .refine((v) => !v.isInternalOriginator || !!v.internalOriginatorId, {
    path: ['internalOriginatorId'],
    message: 'Selecione o originador interno',
  });

type FormValues = z.infer<typeof schema>;

/**
 * +Novo Deal (E6 PR2). RHF + zod (1ª adoção da stack DEC-015). Campos escalares via register;
 * seleções (teses, equipe, comboboxes, originação interna) via watch/setValue. Empresa/Originadora/
 * Pessoa usam o EntityCombobox com criar-inline (mini-formulários abaixo). Status processual entra
 * no form com default "Sem proteção" (decisão de Henrique — pequeno desvio do mock). Tags ficam de
 * fora da criação (editáveis na ficha). Em sucesso, fecha e a lista invalida → deal aparece em Originação.
 */
export function NewDealModal() {
  const open = useNewDeal((s) => s.open);
  const close = useNewDeal((s) => s.closeNew);
  const toast = useToast();
  const reference = useReferenceData();
  const companies = useCompanies(open);
  const originators = useOriginators(open);
  const people = usePeople(open);
  const createDeal = useCreateDeal();

  const [inlineKind, setInlineKind] = useState<null | 'company' | 'originator' | 'person'>(null);
  const [inlineDraft, setInlineDraft] = useState('');

  // Default da data de entrada = hoje (YYYY-MM-DD), em horário local do usuário (achado F7).
  const today = isoLocalDate();

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    getValues,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      codename: '',
      legalStatus: 0,
      investmentValue: '',
      thesisSummary: '',
      leadUserId: '',
      thesisIds: [],
      teamUserIds: [],
      targetCompany: null,
      externalOriginator: null,
      person: null,
      isInternalOriginator: false,
      internalOriginatorId: null,
      initialEntryAt: today,
    },
  });

  // Achado F3 (revisão 2026-07): o modal não fechava com Esc (inconsistente com os demais).
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, close]);

  if (!open) return null;

  // Achado F19 (revisão 2026-07): watch() sem argumento assina TODOS os campos, inclusive os
  // register()-bound (codename, investmentValue, thesisSummary, initialEntryAt) que não são lidos
  // aqui embaixo — cada tecla digitada neles reprocessava o componente inteiro à toa. Assinar só os
  // 7 campos de fato lidos via `v.` evita isso sem tocar nos ~15 pontos de uso abaixo.
  const v = {
    thesisIds: watch('thesisIds'),
    targetCompany: watch('targetCompany'),
    externalOriginator: watch('externalOriginator'),
    isInternalOriginator: watch('isInternalOriginator'),
    person: watch('person'),
    internalOriginatorId: watch('internalOriginatorId'),
    teamUserIds: watch('teamUserIds'),
  };

  // Item 2: uma Pessoa pertence a exatamente uma Originadora. O picker de Pessoa só mostra as pessoas
  // da originadora selecionada (e fica desabilitado enquanto não houver originadora).
  const selectedOriginatorId = v.externalOriginator?.id ?? null;

  const toComboItems = {
    company: (companies.data?.items ?? []).map<ComboItem>((c) => ({
      id: c.id,
      name: c.name,
      subtitle: reference.data?.sectors.find((s) => s.id === c.sectorId)?.name,
    })),
    originator: (originators.data?.items ?? []).map<ComboItem>((o) => ({
      id: o.id,
      name: o.name,
      subtitle: reference.data?.originatorTypes.find((t) => t.id === o.typeId)?.name,
    })),
    person: (people.data?.items ?? [])
      .filter((p) => selectedOriginatorId != null && p.currentOriginatorId === selectedOriginatorId)
      .map<ComboItem>((p) => ({
        id: p.id,
        name: p.name,
        subtitle: p.email ?? undefined,
      })),
  };

  // Ao trocar a originadora, limpa a pessoa se ela não pertencer à nova (avisa discretamente).
  const clearPersonIfMismatch = (newOriginatorId: string | null) => {
    const cur = getValues('person');
    if (!cur) return;
    const personOrig = people.data?.items.find((p) => p.id === cur.id)?.currentOriginatorId ?? null;
    if (personOrig !== newOriginatorId) {
      setValue('person', null);
      toast('Pessoa removida — não pertence à originadora selecionada.', 'warn');
    }
  };

  const toggleThesis = (id: number) => {
    const cur = getValues('thesisIds');
    setValue('thesisIds', cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]);
  };
  const toggleTeam = (id: string) => {
    const cur = getValues('teamUserIds');
    setValue('teamUserIds', cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]);
  };

  const onValid = (form: FormValues) => {
    createDeal.mutate(buildCreateDealBody(form), {
      onSuccess: () => {
        reset();
        close();
      },
    });
  };

  const onInlineCreated = (item: ComboItem) => {
    if (inlineKind === 'company') setValue('targetCompany', item, { shouldValidate: true });
    if (inlineKind === 'originator') setValue('externalOriginator', item);
    if (inlineKind === 'person') setValue('person', item);
    setInlineKind(null);
    setInlineDraft('');
  };

  return (
    <>
      <button
        type="button"
        aria-label="Cancelar"
        onClick={close}
        className="fixed inset-0 z-[70] cursor-default bg-[rgba(20,18,12,0.45)]"
      />
      <div
        role="dialog"
        aria-label="Novo Deal"
        className="fixed left-1/2 top-1/2 z-[80] flex max-h-[90vh] w-[min(560px,92vw)] -translate-x-1/2 -translate-y-1/2 flex-col rounded-lg bg-vega-surface shadow-vega-lg"
      >
        <div className="flex items-center border-b border-vega-line px-5 py-4">
          <div>
            <div className="text-[10.5px] uppercase tracking-wide text-vega-muted">Novo Deal</div>
            <h2 className="text-[16px] font-semibold text-vega-ink">Registrar oportunidade</h2>
          </div>
          <div className="flex-1" />
          <button type="button" onClick={close} aria-label="Fechar" className="p-1 text-vega-muted">
            <X size={16} />
          </button>
        </div>

        <form
          onSubmit={handleSubmit(onValid)}
          className="flex min-h-0 flex-1 flex-col overflow-y-auto"
        >
          <div className="space-y-4 px-5 py-4">
            <div>
              <span className={LABEL}>Codinome *</span>
              <input {...register('codename')} autoFocus placeholder="ex.: Projeto Atlas" className={INPUT} />
              {errors.codename && (
                <span className="mt-1 block text-[11px] text-vega-danger">{errors.codename.message}</span>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <span className={LABEL}>Status processual</span>
                <select {...register('legalStatus')} className={INPUT}>
                  {LEGAL_STATUSES.map((s) => (
                    <option key={s.apiValue} value={s.apiValue}>
                      {s.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <span className={LABEL}>Valor estimado (R$)</span>
                <input {...register('investmentValue')} inputMode="numeric" placeholder="120000000" className={`${INPUT} font-mono`} />
              </div>
            </div>

            <div>
              <span className={LABEL}>Data de entrada</span>
              <input type="date" {...register('initialEntryAt')} max={today} className={INPUT} />
              <span className="mt-1 block text-[11px] text-vega-muted-2">
                Padrão hoje. Pode ser retroativa (não futura) — define a posição na listagem.
              </span>
            </div>

            <div>
              <span className={LABEL}>Tese</span>
              {reference.isLoading ? (
                <span className="text-[12px] text-vega-muted-2">Carregando…</span>
              ) : (
                <div className="flex flex-wrap gap-1.5">
                  {reference.data?.theses.map((t) => {
                    const active = v.thesisIds.includes(t.id);
                    return (
                      <button
                        type="button"
                        key={t.id}
                        onClick={() => toggleThesis(t.id)}
                        className={`rounded px-2.5 py-1 text-[11.5px] font-medium ${
                          active ? 'bg-vega-ink text-vega-bg' : 'bg-vega-bg-2 text-vega-ink-2'
                        }`}
                      >
                        {t.name}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            <div>
              <EntityCombobox
                label="Empresa-alvo"
                items={toComboItems.company}
                loading={companies.isLoading}
                fetching={companies.isFetching}
                value={v.targetCompany}
                onSelect={(item) => setValue('targetCompany', item, { shouldValidate: true })}
                onCreateNew={(draft) => {
                  setInlineDraft(draft);
                  setInlineKind('company');
                }}
              />
              <span className="mt-1 block text-[11px] text-vega-muted-2">
                Opcional — pode definir depois na ficha.
              </span>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <EntityCombobox
                  label="Originadora"
                  items={toComboItems.originator}
                  loading={originators.isLoading}
                  fetching={originators.isFetching}
                  value={v.externalOriginator}
                  disabled={v.isInternalOriginator}
                  onSelect={(item) => {
                    setValue('externalOriginator', item);
                    clearPersonIfMismatch(item?.id ?? null);
                  }}
                  onCreateNew={(draft) => {
                    setInlineDraft(draft);
                    setInlineKind('originator');
                  }}
                />
                <label className="mt-1.5 flex items-center gap-1.5 text-[11.5px] text-vega-muted">
                  <input
                    type="checkbox"
                    checked={v.isInternalOriginator}
                    onChange={(e) => {
                      setValue('isInternalOriginator', e.target.checked);
                      if (e.target.checked) {
                        setValue('externalOriginator', null);
                        clearPersonIfMismatch(null);
                      } else {
                        setValue('internalOriginatorId', null);
                      }
                    }}
                  />
                  Originação interna
                </label>
              </div>
              <div>
                <EntityCombobox
                  label="Pessoa originadora"
                  items={toComboItems.person}
                  loading={people.isLoading}
                  fetching={people.isFetching}
                  value={v.person}
                  disabled={!selectedOriginatorId}
                  onSelect={(item) => setValue('person', item)}
                  onCreateNew={(draft) => {
                    setInlineDraft(draft);
                    setInlineKind('person');
                  }}
                />
                {!selectedOriginatorId && (
                  <span className="mt-1 block text-[11px] text-vega-muted-2">
                    Selecione a originadora primeiro.
                  </span>
                )}
              </div>
            </div>

            {v.isInternalOriginator && (
              <div>
                <span className={LABEL}>Originador interno</span>
                <select
                  value={v.internalOriginatorId ?? ''}
                  onChange={(e) => setValue('internalOriginatorId', e.target.value || null, { shouldValidate: true })}
                  className={INPUT}
                >
                  <option value="">Selecione…</option>
                  {reference.data?.users.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.name}
                    </option>
                  ))}
                </select>
                {errors.internalOriginatorId && (
                  <span className="mt-1 block text-[11px] text-vega-danger">
                    {errors.internalOriginatorId.message as string}
                  </span>
                )}
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <span className={LABEL}>Responsável pelo deal</span>
                <select {...register('leadUserId')} className={INPUT}>
                  <option value="">Selecione…</option>
                  {reference.data?.leadUsers.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.name}
                    </option>
                  ))}
                </select>
                {errors.leadUserId && (
                  <span className="mt-1 block text-[11px] text-vega-danger">{errors.leadUserId.message}</span>
                )}
              </div>
              <div>
                <span className={LABEL}>Equipe</span>
                <div className="flex flex-wrap gap-1.5">
                  {reference.data?.users.map((u) => {
                    const active = v.teamUserIds.includes(u.id);
                    return (
                      <button
                        type="button"
                        key={u.id}
                        onClick={() => toggleTeam(u.id)}
                        className={`rounded-full px-2.5 py-1 text-[11.5px] font-medium ${
                          active ? 'bg-vega-accent-soft text-vega-accent' : 'bg-vega-bg-2 text-vega-ink-2'
                        }`}
                      >
                        {u.name}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            <div>
              <span className={LABEL}>Resumo da tese</span>
              <textarea {...register('thesisSummary')} rows={3} className={`${INPUT} resize-y leading-relaxed`} />
            </div>
          </div>

          <div className="flex items-center gap-2 border-t border-vega-line bg-vega-surface-2 px-5 py-3">
            <span className="text-[11px] text-vega-muted">
              Nasce em <strong>Originação</strong> · status ativo
            </span>
            <div className="flex-1" />
            <button
              type="button"
              onClick={close}
              className="rounded border border-vega-line px-3 py-1.5 text-[12.5px] text-vega-ink-2"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={createDeal.isPending}
              className="rounded bg-vega-ink px-3.5 py-1.5 text-[12.5px] font-semibold text-vega-bg disabled:opacity-50"
            >
              {createDeal.isPending ? 'Criando…' : 'Criar Deal'}
            </button>
          </div>
        </form>
      </div>

      {inlineKind === 'company' && (
        <CompanyInline draft={inlineDraft} onCreated={onInlineCreated} onCancel={() => setInlineKind(null)} />
      )}
      {inlineKind === 'originator' && (
        <OriginatorInline draft={inlineDraft} onCreated={onInlineCreated} onCancel={() => setInlineKind(null)} />
      )}
      {inlineKind === 'person' && (
        <PersonInline
          draft={inlineDraft}
          originatorId={v.externalOriginator?.id ?? null}
          originatorName={v.externalOriginator?.name ?? null}
          onCreated={onInlineCreated}
          onCancel={() => setInlineKind(null)}
        />
      )}
    </>
  );
}
